import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'

// Types
export interface Equipment {
  id: string
  name: string
  brand: string
  category: string
  type: string
  description: string
  image?: string
  price?: {
    min: number
    max: number
    currency: string
  }
  features: string[]
  specifications: Record<string, string>
  compatibility: string[]
  rating: {
    average: number
    count: number
  }
  affiliateLinks?: {
    amazon?: string
    other?: string
  }
  createdAt: string
  updatedAt: string
}

export interface EquipmentFilters {
  categories: string[]
  brands: string[]
  priceRange: {
    min: number | null
    max: number | null
  }
  rating: number | null
  sortBy: 'name' | 'price' | 'rating' | 'created_at'
  sortOrder: 'asc' | 'desc'
}

export interface EquipmentState {
  equipment: Equipment[]
  featured: Equipment[]
  categories: string[]
  brands: string[]
  currentEquipment: Equipment | null
  filters: EquipmentFilters
  loading: boolean
  error: string | null
  pagination: {
    page: number
    limit: number
    total: number
    hasNextPage: boolean
  }
}

const initialState: EquipmentState = {
  equipment: [],
  featured: [],
  categories: [],
  brands: [],
  currentEquipment: null,
  filters: {
    categories: [],
    brands: [],
    priceRange: {
      min: null,
      max: null,
    },
    rating: null,
    sortBy: 'rating',
    sortOrder: 'desc',
  },
  loading: false,
  error: null,
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    hasNextPage: false,
  },
}

// Async thunks
export const fetchEquipment = createAsyncThunk(
  'equipment/fetchEquipment',
  async (params: { page?: number; filters?: Partial<EquipmentFilters> } = {}) => {
    // Mock data for now
    const mockEquipment: Equipment[] = [
      {
        id: '1',
        name: 'V60 Pour Over',
        brand: 'Hario',
        category: 'Pour Over',
        type: 'Dripper',
        description: 'Classic pour over dripper with spiral ribs',
        image: '/images/equipment/v60.jpg',
        price: {
          min: 15,
          max: 35,
          currency: 'USD',
        },
        features: ['Spiral ribs', 'Large single hole', 'Various materials'],
        specifications: {
          'Material': 'Ceramic/Plastic/Glass',
          'Size': '01, 02, 03',
          'Capacity': '1-4 cups',
        },
        compatibility: ['V60 filters', 'Paper filters'],
        rating: {
          average: 4.7,
          count: 1250,
        },
        createdAt: '2020-01-01T00:00:00Z',
        updatedAt: '2023-06-01T00:00:00Z',
      },
    ]
    
    return {
      equipment: mockEquipment,
      categories: ['Pour Over', 'Espresso', 'French Press', 'Grinders'],
      brands: ['Hario', 'Chemex', 'Baratza', 'Fellow'],
      pagination: {
        page: params.page || 1,
        limit: 20,
        total: 1,
        hasNextPage: false,
      },
    }
  }
)

export const fetchEquipmentById = createAsyncThunk(
  'equipment/fetchEquipmentById',
  async (equipmentId: string) => {
    // Mock data for now
    const mockEquipment: Equipment = {
      id: equipmentId,
      name: 'V60 Pour Over',
      brand: 'Hario',
      category: 'Pour Over',
      type: 'Dripper',
      description: 'Classic pour over dripper with spiral ribs',
      image: '/images/equipment/v60.jpg',
      price: {
        min: 15,
        max: 35,
        currency: 'USD',
      },
      features: ['Spiral ribs', 'Large single hole', 'Various materials'],
      specifications: {
        'Material': 'Ceramic/Plastic/Glass',
        'Size': '01, 02, 03',
        'Capacity': '1-4 cups',
      },
      compatibility: ['V60 filters', 'Paper filters'],
      rating: {
        average: 4.7,
        count: 1250,
      },
      createdAt: '2020-01-01T00:00:00Z',
      updatedAt: '2023-06-01T00:00:00Z',
    }
    
    return mockEquipment
  }
)

export const fetchFeaturedEquipment = createAsyncThunk(
  'equipment/fetchFeaturedEquipment',
  async () => {
    // Mock data for now
    const mockFeatured: Equipment[] = [
      {
        id: '1',
        name: 'V60 Pour Over',
        brand: 'Hario',
        category: 'Pour Over',
        type: 'Dripper',
        description: 'Classic pour over dripper with spiral ribs',
        image: '/images/equipment/v60.jpg',
        price: {
          min: 15,
          max: 35,
          currency: 'USD',
        },
        features: ['Spiral ribs', 'Large single hole', 'Various materials'],
        specifications: {
          'Material': 'Ceramic/Plastic/Glass',
          'Size': '01, 02, 03',
          'Capacity': '1-4 cups',
        },
        compatibility: ['V60 filters', 'Paper filters'],
        rating: {
          average: 4.7,
          count: 1250,
        },
        createdAt: '2020-01-01T00:00:00Z',
        updatedAt: '2023-06-01T00:00:00Z',
      },
    ]
    
    return mockFeatured
  }
)

// Slice
const equipmentSlice = createSlice({
  name: 'equipment',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<Partial<EquipmentFilters>>) => {
      state.filters = { ...state.filters, ...action.payload }
    },
    clearFilters: (state) => {
      state.filters = initialState.filters
    },
    setCurrentEquipment: (state, action: PayloadAction<Equipment | null>) => {
      state.currentEquipment = action.payload
    },
    clearError: (state) => {
      state.error = null
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch equipment
      .addCase(fetchEquipment.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchEquipment.fulfilled, (state, action) => {
        state.loading = false
        state.equipment = action.payload.equipment
        state.categories = action.payload.categories
        state.brands = action.payload.brands
        state.pagination = action.payload.pagination
      })
      .addCase(fetchEquipment.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch equipment'
      })
      
      // Fetch equipment by ID
      .addCase(fetchEquipmentById.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchEquipmentById.fulfilled, (state, action) => {
        state.loading = false
        state.currentEquipment = action.payload
      })
      .addCase(fetchEquipmentById.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch equipment'
      })
      
      // Fetch featured equipment
      .addCase(fetchFeaturedEquipment.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchFeaturedEquipment.fulfilled, (state, action) => {
        state.loading = false
        state.featured = action.payload
      })
      .addCase(fetchFeaturedEquipment.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch featured equipment'
      })
  },
})

export const { setFilters, clearFilters, setCurrentEquipment, clearError } = equipmentSlice.actions
export default equipmentSlice.reducer 